/**
 * Un champ d'objet ou d'étape dans l'inspecteur du Studio, dessiné d'après
 * sa description lue dans le schéma (`objectFields`, `stepFields`) : texte,
 * formule (avec aperçu), nombre ou expression, couleur de la palette, point,
 * cible, liste, sous-objet. Un champ facultatif vidé est RETIRÉ du document.
 */

import { useId, useState } from 'react';
import { Plus, X } from 'lucide-react';
import {
  JRAF_COLOR_NAMES,
  JRAF_PALETTE,
  type DraftPath,
  type FieldSpec,
} from '@jeraisonne/animation';
import { cn } from '@jeraisonne/ui';
import { formulaToHtml } from '@jeraisonne/ui/components/animations/animation-math';

export interface FieldContext {
  objects: { id: string; type: string }[];
  /** étapes de la scène écrites AVANT celle-ci (`with`) */
  stepsBefore: string[];
  /** phrases de la narration de la scène (`atSentence`) */
  sentences: string[];
  tools: { id: string; title: string }[];
  images: string[];
  /** choix de la question (`correct`) */
  choices: string[];
}

const input =
  'border-ink-200 focus:border-primary-400 min-h-10 w-full rounded-lg border bg-white px-2.5 text-sm outline-none';

const COLOR_LABELS: Record<string, string> = {
  night: 'nuit',
  paper: 'blanc',
  ink: 'encre',
  muted: 'gris',
  primary: 'bleu',
  'primary-light': 'bleu clair',
  accent: 'corail',
  gold: 'or',
  'gold-light': 'or clair',
  green: 'vert',
  red: 'rouge',
  'hora-h': 'H (bleu)',
  'hora-o': 'O (vert)',
  'hora-r': 'R (orange)',
  'hora-a': 'A (rouge)',
};

/** « 260 / a » reste une expression ; « 12,5 » devient le nombre 12.5. */
export function parseNumExpr(text: string): number | string | undefined {
  const s = text.trim();
  if (!s) return undefined;
  if (/^-?\d+([.,]\d+)?$/.test(s)) return Number(s.replace(',', '.'));
  return s;
}

function numText(v: unknown): string {
  return typeof v === 'number' ? String(v).replace('.', ',') : typeof v === 'string' ? v : '';
}

export function FieldEditor({
  spec,
  value,
  path,
  onSet,
  ctx,
  hideLabel = false,
}: {
  spec: FieldSpec;
  value: unknown;
  path: DraftPath;
  onSet: (path: DraftPath, value: unknown, coalesce?: boolean) => void;
  ctx: FieldContext;
  hideLabel?: boolean;
}) {
  const id = useId();
  const set = (v: unknown, coalesce = false) => onSet(path, v, coalesce);
  const hint =
    spec.optional && spec.default !== undefined ? ` (défaut : ${String(spec.default)})` : '';

  if (spec.kind === 'object' && spec.fields) {
    const obj = value && typeof value === 'object' ? (value as Record<string, unknown>) : null;
    return (
      <fieldset className="border-ink-200 rounded-xl border px-3 pb-3 pt-1">
        <legend className="text-ink-700 px-1 text-xs font-semibold">{spec.label}</legend>
        {!obj && spec.optional ? (
          <button
            type="button"
            onClick={() => set(defaultsOf(spec.fields!))}
            className="text-primary-600 min-h-10 text-sm font-semibold"
          >
            <Plus className="mr-1 inline h-4 w-4" aria-hidden />
            Ajouter
          </button>
        ) : (
          <div className="grid grid-cols-2 gap-2">
            {spec.fields.map((f) => (
              <div key={f.key} className={wide(f) ? 'col-span-2' : ''}>
                <FieldEditor
                  spec={f}
                  value={obj?.[f.key]}
                  path={[...path, f.key]}
                  onSet={onSet}
                  ctx={ctx}
                />
              </div>
            ))}
            {spec.optional && (
              <button
                type="button"
                onClick={() => set(undefined)}
                className="text-ink-500 hover:text-error-600 col-span-2 min-h-9 justify-self-start text-[13px] font-semibold"
              >
                Retirer
              </button>
            )}
          </div>
        )}
      </fieldset>
    );
  }

  // élément d'une liste : libellé lu par les lecteurs d'écran seulement
  const label = (
    <label
      htmlFor={id}
      className={cn('text-ink-700 mb-0.5 block text-xs font-semibold', hideLabel && 'sr-only')}
    >
      {spec.label}
      {!spec.optional && !hideLabel && <span className="text-error-600"> *</span>}
      {hint && <span className="text-ink-400 font-normal">{hint}</span>}
    </label>
  );

  switch (spec.kind) {
    case 'display':
      return (
        <div>
          {label}
          <textarea
            id={id}
            rows={2}
            className={cn(input, 'py-1.5')}
            value={typeof value === 'string' ? value : ''}
            onChange={(e) =>
              set(e.target.value || (spec.optional ? undefined : e.target.value), true)
            }
          />
        </div>
      );
    case 'tex': {
      const tex = typeof value === 'string' ? value : '';
      return (
        <div>
          {label}
          <input
            id={id}
            className={cn(input, 'font-mono text-[13px]')}
            value={tex}
            spellCheck={false}
            onChange={(e) =>
              set(e.target.value || (spec.optional ? undefined : e.target.value), true)
            }
          />
          {tex && (
            <div
              className="bg-ink-900 mt-1 overflow-x-auto rounded-lg px-2 py-1.5 text-center text-white"
              dangerouslySetInnerHTML={{ __html: formulaToHtml(tex) }}
            />
          )}
        </div>
      );
    }
    case 'string':
      return (
        <div>
          {label}
          <input
            id={id}
            className={input}
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => set(e.target.value || (spec.optional ? undefined : ''), true)}
          />
        </div>
      );
    case 'number':
    case 'int':
      return (
        <div>
          {label}
          <input
            id={id}
            type="number"
            inputMode="decimal"
            className={input}
            min={spec.min}
            max={spec.max}
            step={spec.kind === 'int' ? 1 : 'any'}
            value={typeof value === 'number' ? value : ''}
            placeholder={spec.default !== undefined ? String(spec.default) : ''}
            onChange={(e) => {
              const n = e.target.value === '' ? undefined : Number(e.target.value);
              set(n === undefined || Number.isNaN(n) ? (spec.optional ? undefined : 0) : n, true);
            }}
          />
        </div>
      );
    case 'numExpr':
      return (
        <div>
          {label}
          <NumExprInput
            id={id}
            value={value}
            optional={spec.optional}
            onChange={(v) => set(v, true)}
          />
        </div>
      );
    case 'boolean':
      return (
        <label className="flex min-h-10 items-center gap-2 text-sm">
          <input
            type="checkbox"
            className="accent-primary-500 h-4 w-4"
            checked={typeof value === 'boolean' ? value : spec.default === true}
            onChange={(e) => set(e.target.checked)}
          />
          {spec.label}
        </label>
      );
    case 'enum':
      return (
        <div>
          {label}
          <select
            id={id}
            className={input}
            value={typeof value === 'string' ? value : ''}
            onChange={(e) => set(e.target.value || undefined)}
          >
            {spec.optional && (
              <option value="">
                {spec.default !== undefined ? `défaut (${String(spec.default)})` : '—'}
              </option>
            )}
            {(spec.options ?? []).map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </div>
      );
    case 'color':
    case 'paint':
      return (
        <div>
          {label}
          <ColorInput
            id={id}
            value={typeof value === 'string' ? value : ''}
            paint={spec.kind === 'paint'}
            optional={spec.optional}
            onChange={(v) => set(v)}
          />
        </div>
      );
    case 'point': {
      const p = value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
      return (
        <fieldset>
          <legend
            className={cn('text-ink-700 mb-0.5 text-xs font-semibold', hideLabel && 'sr-only')}
          >
            {spec.label}
          </legend>
          <div className="grid grid-cols-2 gap-2">
            {(['x', 'y'] as const).map((k) => (
              <NumExprInput
                key={k}
                id={`${id}-${k}`}
                label={k}
                value={p[k]}
                optional={false}
                onChange={(v) => onSet([...path, k], v ?? 0, true)}
              />
            ))}
          </div>
        </fieldset>
      );
    }
    case 'target':
      return (
        <div>
          {label}
          <TargetInput id={id} value={value} objects={ctx.objects} onChange={(v) => set(v)} />
        </div>
      );
    case 'ref':
      return (
        <div>
          {label}
          <RefSelect id={id} spec={spec} value={value} ctx={ctx} onChange={(v) => set(v)} />
        </div>
      );
    case 'variant': {
      const obj = value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
      const mode = typeof obj.mode === 'string' ? obj.mode : '';
      const variant = spec.variants?.find((v) => v.value === mode);
      return (
        <div className="space-y-2">
          <div>
            {label}
            <select
              id={id}
              className={input}
              value={mode}
              onChange={(e) => {
                const v = spec.variants?.find((x) => x.value === e.target.value);
                if (v) set({ mode: v.value, ...defaultsOf(v.fields) });
              }}
            >
              {(spec.variants ?? []).map((v) => (
                <option key={v.value} value={v.value}>
                  {VARIANT_LABELS[v.value] ?? v.value}
                </option>
              ))}
            </select>
          </div>
          {variant?.fields.map((f) => (
            <FieldEditor
              key={f.key}
              spec={f}
              value={obj[f.key]}
              path={[...path, f.key]}
              onSet={onSet}
              ctx={ctx}
            />
          ))}
        </div>
      );
    }
    case 'list':
      break;
    default:
      break;
  }

  if (spec.kind === 'list') {
    return <ListEditor spec={spec} value={value} path={path} onSet={onSet} ctx={ctx} />;
  }
  return null;
}

const VARIANT_LABELS: Record<string, string> = {
  'auto-reveal': 'Réponse montrée après un temps',
  cta: 'Carte « Fais-le dans l’app »',
  skip: 'Sautée',
};

function wide(f: FieldSpec): boolean {
  return ['display', 'tex', 'object', 'list', 'target', 'variant', 'point'].includes(f.kind);
}

/** Valeurs de départ d'un sous-objet ajouté : ses champs obligatoires. */
function defaultsOf(fields: FieldSpec[]): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const f of fields) {
    if (f.default !== undefined) out[f.key] = f.default;
    else if (!f.optional)
      out[f.key] = f.kind === 'boolean' ? false : f.kind.match(/number|int|numExpr/) ? 0 : '';
  }
  return out;
}

function NumExprInput({
  id,
  label,
  value,
  optional,
  onChange,
}: {
  id: string;
  label?: string;
  value: unknown;
  optional: boolean;
  onChange: (v: number | string | undefined) => void;
}) {
  // le texte tapé est gardé tel quel (« 12, » en cours de frappe)
  const [text, setText] = useState(numText(value));
  const [seen, setSeen] = useState(value);
  if (value !== seen) {
    setSeen(value);
    if (parseNumExpr(text) !== value) setText(numText(value));
  }
  return (
    <div className="flex items-center gap-1.5">
      {label && (
        <label htmlFor={id} className="text-ink-500 w-3 text-xs font-semibold">
          {label}
        </label>
      )}
      <input
        id={id}
        className={cn(input, 'font-mono text-[13px]')}
        value={text}
        spellCheck={false}
        placeholder="nombre ou expression"
        onChange={(e) => {
          setText(e.target.value);
          const v = parseNumExpr(e.target.value);
          onChange(v === undefined && !optional ? 0 : v);
        }}
      />
    </div>
  );
}

function ColorInput({
  id,
  value,
  paint,
  optional,
  onChange,
}: {
  id: string;
  value: string;
  paint: boolean;
  optional: boolean;
  onChange: (v: string | undefined) => void;
}) {
  const named = value === '' || value === 'none' || (JRAF_COLOR_NAMES as string[]).includes(value);
  const swatch =
    (JRAF_PALETTE as Record<string, string>)[value] ?? (value.startsWith('#') ? value : null);
  return (
    <div className="flex items-center gap-2">
      <span
        aria-hidden
        className="border-ink-200 h-7 w-7 shrink-0 rounded-full border"
        style={{
          background: swatch ?? 'repeating-linear-gradient(45deg,#ddd 0 4px,#fff 4px 8px)',
        }}
      />
      <select
        id={id}
        className={input}
        value={named ? value : '#'}
        onChange={(e) => {
          const v = e.target.value;
          if (v === '#') onChange('#ffffff');
          else onChange(v || undefined);
        }}
      >
        {optional && <option value="">défaut</option>}
        {paint && <option value="none">aucune</option>}
        {JRAF_COLOR_NAMES.map((c) => (
          <option key={c} value={c}>
            {COLOR_LABELS[c] ?? c}
          </option>
        ))}
        <option value="#">autre (#rrggbb)…</option>
      </select>
      {!named && (
        <input
          aria-label="Couleur hexadécimale"
          className={cn(input, 'w-28 font-mono text-[13px]')}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </div>
  );
}

function TargetInput({
  id,
  value,
  objects,
  onChange,
}: {
  id: string;
  value: unknown;
  objects: FieldContext['objects'];
  onChange: (v: string | string[]) => void;
}) {
  const list = Array.isArray(value)
    ? (value as string[])
    : typeof value === 'string' && value
      ? [value]
      : [];
  const set = (ids: string[]) => onChange(ids.length === 1 ? ids[0]! : ids);
  const option = (o: FieldContext['objects'][number]) => (
    <option key={o.id} value={o.id}>
      {o.id} ({o.type})
    </option>
  );
  return (
    <div className="space-y-1.5">
      {/* l'objet visé (le premier) */}
      <select
        id={id}
        className={input}
        value={list[0] ?? ''}
        onChange={(e) => e.target.value && set([e.target.value, ...list.slice(1)])}
      >
        {!list[0] && <option value="">Choisir l’objet…</option>}
        {list[0] && !objects.some((o) => o.id === list[0]) && (
          <option value={list[0]}>{list[0]} (introuvable)</option>
        )}
        {objects.filter((o) => o.id === list[0] || !list.includes(o.id)).map(option)}
      </select>
      {/* les autres, ensemble */}
      {list.length > 1 && (
        <div className="flex flex-wrap gap-1.5">
          {list.slice(1).map((t) => (
            <span
              key={t}
              className="bg-primary-50 text-primary-700 inline-flex items-center gap-1 rounded-full py-0.5 pl-2.5 pr-1 text-[13px] font-semibold"
            >
              {t}
              <button
                type="button"
                onClick={() => set(list.filter((x) => x !== t))}
                className="hover:bg-primary-100 rounded-full p-0.5"
                aria-label={`Retirer ${t}`}
              >
                <X className="h-3.5 w-3.5" aria-hidden />
              </button>
            </span>
          ))}
        </div>
      )}
      {list.length > 0 && (
        <select
          aria-label="Ajouter un autre objet (ensemble)"
          className={input}
          value=""
          onChange={(e) => e.target.value && set([...list, e.target.value])}
        >
          <option value="">+ un autre objet en même temps…</option>
          {objects.filter((o) => !list.includes(o.id)).map(option)}
        </select>
      )}
    </div>
  );
}

function RefSelect({
  id,
  spec,
  value,
  ctx,
  onChange,
}: {
  id: string;
  spec: FieldSpec;
  value: unknown;
  ctx: FieldContext;
  onChange: (v: unknown) => void;
}) {
  const options: { value: string; label: string }[] =
    spec.refTo === 'group'
      ? ctx.objects.filter((o) => o.type === 'group').map((o) => ({ value: o.id, label: o.id }))
      : spec.refTo === 'step'
        ? ctx.stepsBefore.map((s) => ({ value: s, label: s }))
        : spec.refTo === 'sentence'
          ? ctx.sentences.map((s, i) => ({
              value: String(i),
              label: `${i + 1}. ${s.length > 48 ? `${s.slice(0, 47)}…` : s}`,
            }))
          : spec.refTo === 'choice'
            ? ctx.choices.map((c, i) => ({ value: String(i), label: `${i + 1}. ${c}` }))
            : spec.refTo === 'tool'
              ? ctx.tools.map((t) => ({ value: t.id, label: t.title }))
              : ctx.images.map((a) => ({ value: a, label: a }));
  const numeric = spec.refTo === 'sentence' || spec.refTo === 'choice';
  const current = value === undefined || value === null ? '' : String(value);
  return (
    <select
      id={id}
      className={input}
      value={current}
      onChange={(e) => {
        const v = e.target.value;
        onChange(v === '' ? undefined : numeric ? Number(v) : v);
      }}
    >
      {spec.optional && <option value="">—</option>}
      {!options.some((o) => o.value === current) && current !== '' && (
        <option value={current}>{current} (introuvable)</option>
      )}
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

function ListEditor({
  spec,
  value,
  path,
  onSet,
  ctx,
}: {
  spec: FieldSpec;
  value: unknown;
  path: DraftPath;
  onSet: (path: DraftPath, value: unknown, coalesce?: boolean) => void;
  ctx: FieldContext;
}) {
  const items = Array.isArray(value) ? value : [];
  const item = spec.item!;
  const max = spec.max ?? 50;
  const min = spec.min ?? 0;
  const blank = item.kind === 'point' ? { x: 0, y: 0 } : item.kind === 'display' ? '…' : '';
  return (
    <fieldset className="space-y-1.5">
      <legend className="text-ink-700 mb-0.5 text-xs font-semibold">{spec.label}</legend>
      {items.map((v, i) => (
        <div key={i} className="flex items-start gap-1.5">
          <span className="text-ink-400 mt-2.5 w-5 text-right text-xs tabular-nums">{i + 1}.</span>
          <div className="min-w-0 flex-1">
            <FieldEditor
              spec={{ ...item, label: `${spec.label} ${i + 1}`, key: String(i) }}
              value={v}
              path={[...path, i]}
              onSet={onSet}
              ctx={ctx}
              hideLabel
            />
          </div>
          <button
            type="button"
            disabled={items.length <= min && !spec.optional}
            onClick={() => {
              const next = items.filter((_, j) => j !== i);
              onSet(path, next.length === 0 && spec.optional ? undefined : next);
            }}
            className="text-ink-400 hover:text-error-600 mt-1.5 rounded p-1.5 disabled:opacity-30"
            aria-label={`Retirer l’élément ${i + 1}`}
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>
      ))}
      {items.length < max && (
        <button
          type="button"
          onClick={() => onSet(path, [...items, blank])}
          className="text-primary-600 min-h-9 text-[13px] font-semibold"
        >
          <Plus className="mr-1 inline h-4 w-4" aria-hidden />
          Ajouter
        </button>
      )}
    </fieldset>
  );
}
